import React, { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users, Trash2, RotateCcw, AlertTriangle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { getJobs, getAllCandidates, getDeletedCandidates, restoreCandidate, permanentDeleteCandidate, getCandidateDetail } from '../services/api';
import { useRegion } from '../context/RegionContext';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { SmartCandidateTable } from '../components/SmartCandidateTable';
import { ResumeViewerModal } from '../components/ResumeViewerModal';

export default function CandidatesPage() {
  const { region } = useRegion();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Deleted tab
  const [activeTab, setActiveTab] = useState('active');
  const [deletedCandidates, setDeletedCandidates] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Resume viewer
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedResumeCandidate, setSelectedResumeCandidate] = useState(null);

  const handleViewResume = async (candidate) => {
    if (candidate.raw_text) { setSelectedResumeCandidate(candidate); setShowResumeModal(true); return; }
    try {
      const full = await getCandidateDetail(candidate.job_id, candidate.id);
      setSelectedResumeCandidate(full); setShowResumeModal(true);
    } catch (e) { toast.error('Failed to load resume'); }
  };

  useEffect(() => { loadAll(); }, [region]);
  useEffect(() => { if (activeTab === 'deleted') loadDeletedCandidates(); }, [activeTab, region]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [jobsData, candsData] = await Promise.all([
        getJobs(region),
        getAllCandidates(region, '', 1000, 0),
      ]);
      setJobs(jobsData.filter(j => j.candidate_total > 0));
      setCandidates(candsData.candidates || []);
    } catch (e) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedCandidates = async () => {
    try {
      setLoadingDeleted(true);
      const data = await getDeletedCandidates(region);
      setDeletedCandidates(data.candidates || []);
    } catch (e) { console.error(e); }
    finally { setLoadingDeleted(false); }
  };

  const handleRestoreCandidate = async (id) => {
    try {
      const result = await restoreCandidate(id);
      toast.success(`Restored "${result.name}"`);
      loadDeletedCandidates();
      loadAll();
    } catch (e) { toast.error('Failed to restore'); }
  };

  const handlePermanentDeleteCandidate = async (id) => {
    try {
      await permanentDeleteCandidate(id);
      toast.success('Candidate permanently deleted');
      loadDeletedCandidates();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="candidates-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="candidates-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          All Candidates
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {candidates.length} candidates across {jobs.length} jobs
        </p>
      </div>

      {/* Active / Deleted Tabs */}
      <div className="flex items-center gap-1 border-b" data-testid="candidates-tabs">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="candidates-tab-active"
        >
          <Users className="h-3.5 w-3.5 inline mr-1.5" />Active
        </button>
        <button
          onClick={() => setActiveTab('deleted')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
            activeTab === 'deleted' ? 'border-red-500 text-red-500' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="candidates-tab-deleted"
        >
          <Trash2 className="h-3.5 w-3.5" />Recycle Bin
          {deletedCandidates.length > 0 && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">{deletedCandidates.length}</Badge>
          )}
        </button>
      </div>

      {activeTab === 'deleted' ? (
        <div className="space-y-3" data-testid="deleted-candidates-view">
          {loadingDeleted ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : deletedCandidates.length === 0 ? (
            <div className="text-center py-16">
              <Trash2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No deleted candidates</p>
            </div>
          ) : deletedCandidates.map(c => {
            const name = c.parsed_data?.name || 'Unknown';
            let deletedAgo = '';
            try { deletedAgo = formatDistanceToNow(parseISO(c.deleted_at), { addSuffix: true }); } catch { /* date parse failed */ }
            return (
              <div key={c.id} className="flex items-center gap-4 p-4 rounded-lg border border-dashed border-red-500/20 bg-red-500/5" data-testid={`deleted-candidate-${c.id}`}>
                <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    {c.job_title && <span>{c.job_title}</span>}
                    {c.parsed_data?.email && <span>{c.parsed_data.email}</span>}
                    {deletedAgo && <span className="text-red-400">Deleted {deletedAgo}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="gap-1.5 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                    onClick={() => handleRestoreCandidate(c.id)} data-testid={`restore-candidate-${c.id}`}>
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-red-500 border-red-500/30 hover:bg-red-500/10"
                    onClick={() => handlePermanentDeleteCandidate(c.id)} data-testid={`perm-delete-candidate-${c.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Delete Forever
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <SmartCandidateTable
          candidates={candidates}
          jobs={jobs}
          loading={loading}
          showJobColumn={true}
          showBulkActions={true}
          onViewResume={handleViewResume}
          onBulkLinksGenerated={loadAll}
          pageSize={25}
        />
      )}
      <ResumeViewerModal isOpen={showResumeModal} onClose={() => setShowResumeModal(false)} candidate={selectedResumeCandidate} />
    </div>
  );
}
